import { VisualizationNode } from '../types/infrastructure';

export interface ComponentLayoutConfig {
  baseSpacing: number;
  centerX: number;
  centerY: number;
}

export type LayoutStrategy = 'linear' | 'radial' | 'grid' | 'ring';

export interface ComponentConnection {
  fromComponent: string;
  toComponent: string;
  linkType?: string;
}

export class ComponentLayoutEngine {
  private config: ComponentLayoutConfig;

  constructor(config: ComponentLayoutConfig) {
    this.config = config;
  }

  /**
   * Determine the best layout strategy for a device based on its components and connections
   */
  determineLayoutStrategy(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): LayoutStrategy {
    const componentCount = components.length;

    // Analyze connection patterns
    const connectionCounts = new Map<string, number>();
    connections.forEach(conn => {
      connectionCounts.set(conn.fromComponent, (connectionCounts.get(conn.fromComponent) || 0) + 1);
      connectionCounts.set(conn.toComponent, (connectionCounts.get(conn.toComponent) || 0) + 1);
    });

    // Find components with high connectivity (hubs)
    const hubComponents = Array.from(connectionCounts.entries())
      .filter(([_, count]) => count >= componentCount / 2)
      .map(([componentId]) => componentId);

    // Determine strategy based on patterns
    if (componentCount <= 2) {
      return 'linear';
    } else if (this.isRingTopology(components, connections)) {
      return 'ring';
    } else if (hubComponents.length > 0) {
      // Check if this is a true hub-and-spoke or just compute-to-network flow
      if (this.isComputeToNetworkFlow(components, connections, hubComponents)) {
        return 'linear'; // Use linear layout with vertical layering
      } else {
        return 'radial'; // True hub-and-spoke pattern
      }
    } else {
      return 'grid';
    }
  }

  /**
   * Check if this is a compute-to-network flow (NPU/GPU/CPU connecting to NICs/ports)
   * vs. a true hub-and-spoke (switch ASIC with many ports)
   */
  private isComputeToNetworkFlow(
    components: VisualizationNode[],
    connections: ComponentConnection[],
    hubComponents: string[]
  ): boolean {
    // Look at the component types to determine the pattern
    const componentTypes = components.map(comp => {
      const componentId = comp.id.split('/').pop() || '';
      return componentId.split('-')[0]; // Extract type (e.g., "nic-0" -> "nic")
    });

    const typeSet = new Set(componentTypes);

    // Compute-to-network flow characteristics:
    // 1. Has compute components (xpu, gpu, cpu) and network components (nic, port)
    // 2. Small number of total components (typically ≤ 5)
    // 3. Hub component is a compute type, not switching type

    // Support both old (npu) and new (xpu) naming for backward compatibility
    const computeTypes = ['xpu', 'gpu', 'cpu', 'npu'];
    const networkTypes = ['nic', 'port'];
    const switchTypes = ['asic', 'switch'];

    const hasCompute = computeTypes.some(type => typeSet.has(type));
    const hasNetwork = networkTypes.some(type => typeSet.has(type));
    const hasSwitch = switchTypes.some(type => typeSet.has(type));

    // Check if hub components are compute types
    const hubIsCompute = hubComponents.some(hubId => {
      const hubType = hubId.split('-')[0];
      return computeTypes.includes(hubType);
    });

    // Compute-to-network: has compute + network, hub is compute, no switch, small scale
    return hasCompute && hasNetwork && hubIsCompute && !hasSwitch && components.length <= 6;
  }

  /**
   * Check if components form a ring topology
   */
  private isRingTopology(components: VisualizationNode[], connections: ComponentConnection[]): boolean {
    // A ring has same number of connections as components, and each component has exactly 2 connections
    if (connections.length !== components.length) return false;

    const connectionCounts = new Map<string, number>();
    connections.forEach(conn => {
      connectionCounts.set(conn.fromComponent, (connectionCounts.get(conn.fromComponent) || 0) + 1);
      connectionCounts.set(conn.toComponent, (connectionCounts.get(conn.toComponent) || 0) + 1);
    });

    return Array.from(connectionCounts.values()).every(count => count === 2);
  }

  /**
   * Apply layout strategy to position components
   */
  layoutComponents(
    components: VisualizationNode[],
    connections: ComponentConnection[],
    strategy?: LayoutStrategy
  ): VisualizationNode[] {
    const layoutStrategy = strategy || this.determineLayoutStrategy(components, connections);

    // Debug logging to understand layout decisions
    const deviceName = components[0]?.parent || 'unknown';
    // const componentTypes = components.map(c => c.name).join(', ');
    // const connectionPattern = connections.map(c => `${c.fromComponent}->${c.toComponent}`).join(', ');
    const typeGroups = this.groupComponentsByType(components);
    const uniqueTypes = Array.from(typeGroups.keys());

    console.log(`ComponentLayout: Device ${deviceName} using ${layoutStrategy} layout`);
    //console.log(`  Components: ${componentTypes}`);
    //console.log(`  Types: ${uniqueTypes.join(', ')}`);
    //console.log(`  Connections: ${connectionPattern}`);

    if (layoutStrategy === 'linear' && uniqueTypes.length > 1) {
      console.log(`  → Using vertical layering for different component types`);
    }

    switch (layoutStrategy) {
      case 'linear':
        return this.applyLinearLayout(components, connections);
      case 'radial':
        return this.applyRadialLayout(components, connections);
      case 'ring':
        return this.applyRingLayout(components, connections);
      case 'grid':
      default:
        return this.applyGridLayout(components);
    }
  }

  /**
   * Linear layout: Arrange components in a horizontal line based on connection flow
   * with vertical layering for different component types to prevent label overlap
   */
  private applyLinearLayout(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): VisualizationNode[] {
    if (components.length <= 1) {
      return components.map((comp, index) => ({
        ...comp,
        x: this.config.centerX,
        y: this.config.centerY
      }));
    }

    // Group components by type for vertical layering
    const componentTypeGroups = this.groupComponentsByType(components);
    const uniqueTypes = Array.from(componentTypeGroups.keys());

    // If all components are the same type, use simple horizontal layout
    if (uniqueTypes.length === 1) {
      return this.applyHorizontalLinearLayout(components, connections);
    }

    // For different component types, use vertical layering
    return this.applyVerticalLayeredLayout(components, connections, componentTypeGroups);
  }

  /**
   * Group components by their type (npu, nic, asic, etc.)
   */
  private groupComponentsByType(components: VisualizationNode[]): Map<string, VisualizationNode[]> {
    const groups = new Map<string, VisualizationNode[]>();

    components.forEach(comp => {
      const componentId = comp.id.split('/').pop() || '';
      // Extract component type from ID (e.g., "nic-0" -> "nic", "npu-0" -> "npu")
      const componentType = componentId.split('-')[0];

      if (!groups.has(componentType)) {
        groups.set(componentType, []);
      }
      groups.get(componentType)!.push(comp);
    });

    return groups;
  }

  /**
   * Apply horizontal linear layout for components of the same type
   */
  private applyHorizontalLinearLayout(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): VisualizationNode[] {
    const positioned = new Set<string>();
    const result: VisualizationNode[] = [];

    // Find source component (has outgoing but minimal incoming)
    const incomingCounts = new Map<string, number>();
    connections.forEach(conn => {
      incomingCounts.set(conn.toComponent, (incomingCounts.get(conn.toComponent) || 0) + 1);
    });

    const sourceComp = components.reduce((min, comp) => {
      const compId = comp.id.split('/').pop() || '';
      const incomingCount = incomingCounts.get(compId) || 0;
      const minCompId = min.id.split('/').pop() || '';
      const minIncomingCount = incomingCounts.get(minCompId) || 0;
      return incomingCount < minIncomingCount ? comp : min;
    });

    let currentComp = sourceComp;
    let xPosition = this.config.centerX - ((components.length - 1) * this.config.baseSpacing) / 2;

    // Position source component
    positioned.add(currentComp.id);
    result.push({
      ...currentComp,
      x: xPosition,
      y: this.config.centerY
    });
    xPosition += this.config.baseSpacing;

    // Follow connection chain
    while (positioned.size < components.length) {
      const currentCompId = currentComp.id.split('/').pop() || '';
      const currentParent = currentComp.parent;
      // eslint-disable-next-line no-loop-func
      const nextConnection = connections.find(conn =>
        conn.fromComponent === currentCompId && !positioned.has(`${currentParent}/${conn.toComponent}`)
      );

      if (nextConnection) {
        const nextComp = components.find(comp =>
          comp.id.split('/').pop() === nextConnection.toComponent
        );
        if (nextComp && !positioned.has(nextComp.id)) {
          positioned.add(nextComp.id);
          result.push({
            ...nextComp,
            x: xPosition,
            y: this.config.centerY
          });
          currentComp = nextComp;
          xPosition += this.config.baseSpacing;
        } else {
          break;
        }
      } else {
        break;
      }
    }

    // Position remaining unconnected components
    components.forEach(comp => {
      if (!positioned.has(comp.id)) {
        result.push({
          ...comp,
          x: xPosition,
          y: this.config.centerY
        });
        xPosition += this.config.baseSpacing;
      }
    });

    return result;
  }

  /**
   * Apply vertical layered layout with different component types on different levels
   */
  private applyVerticalLayeredLayout(
    components: VisualizationNode[],
    connections: ComponentConnection[],
    componentTypeGroups: Map<string, VisualizationNode[]>
  ): VisualizationNode[] {
    const result: VisualizationNode[] = [];
    const layerHeight = this.config.baseSpacing * 1.2; // Spacing between layers

    // Define preferred layer order for common component types
    const layerPriority: Record<string, number> = {
      'xpu': 0,    // Compute components at top (neural/graphics processing units)
      'npu': 0,    // Legacy name for neural processing units (backward compatibility)
      'gpu': 0,    // Compute components at top
      'cpu': 0,    // Compute components at top
      'asic': 1,   // Processing components in middle
      'switch': 1, // Processing components in middle
      'nic': 2,    // Network components at bottom
      'port': 2    // Network components at bottom
    };

    // Sort component types by priority, then alphabetically
    const sortedTypes = Array.from(componentTypeGroups.keys()).sort((a, b) => {
      const priorityA = layerPriority[a] ?? 999;
      const priorityB = layerPriority[b] ?? 999;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.localeCompare(b);
    });

    // Calculate starting Y position to center the layers
    const totalLayers = sortedTypes.length;
    const startY = this.config.centerY - ((totalLayers - 1) * layerHeight) / 2;

    // Position each layer
    sortedTypes.forEach((componentType, layerIndex) => {
      const componentsInLayer = componentTypeGroups.get(componentType)!;
      const layerY = startY + layerIndex * layerHeight;

      // Center components horizontally within the layer
      const layerWidth = (componentsInLayer.length - 1) * this.config.baseSpacing;
      const startX = this.config.centerX - layerWidth / 2;

      // Try to order components within layer based on connections
      const orderedComponents = this.orderComponentsByConnections(
        componentsInLayer,
        connections
      );

      orderedComponents.forEach((comp, index) => {
        result.push({
          ...comp,
          x: startX + index * this.config.baseSpacing,
          y: layerY
        });
      });
    });

    return result;
  }

  /**
   * Order components within a layer based on their connection patterns
   */
  private orderComponentsByConnections(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): VisualizationNode[] {
    if (components.length <= 1) return components;

    // Build adjacency information
    const adjacency = new Map<string, Set<string>>();
    components.forEach(comp => {
      const compId = comp.id.split('/').pop() || '';
      adjacency.set(compId, new Set());
    });

    connections.forEach(conn => {
      if (adjacency.has(conn.fromComponent) && adjacency.has(conn.toComponent)) {
        adjacency.get(conn.fromComponent)!.add(conn.toComponent);
        adjacency.get(conn.toComponent)!.add(conn.fromComponent);
      }
    });

    // Start with component that has most connections, or first component
    const startComp = components.reduce((max, comp) => {
      const compId = comp.id.split('/').pop() || '';
      const maxId = max.id.split('/').pop() || '';
      const compConnections = adjacency.get(compId)?.size || 0;
      const maxConnections = adjacency.get(maxId)?.size || 0;
      return compConnections > maxConnections ? comp : max;
    });

    // Simple ordering: start with most connected, then add neighbors
    const ordered = [startComp];
    const used = new Set([startComp.id.split('/').pop()!]);

    while (ordered.length < components.length) {
      // Find next component that connects to any already ordered component
      let nextComp = components.find(comp => {
        const compId = comp.id.split('/').pop() || '';
        if (used.has(compId)) return false;

        return Array.from(adjacency.get(compId) || []).some(neighbor => used.has(neighbor));
      });

      // If no connected component found, just add the first unused one
      if (!nextComp) {
        nextComp = components.find(comp => !used.has(comp.id.split('/').pop()!));
      }

      if (nextComp) {
        ordered.push(nextComp);
        used.add(nextComp.id.split('/').pop()!);
      } else {
        break; // Should not happen, but prevent infinite loop
      }
    }

    return ordered;
  }

  /**
   * Radial layout: Place hub component at center, others around it
   */
  private applyRadialLayout(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): VisualizationNode[] {
    // Find hub component (most connected)
    const connectionCounts = new Map<string, number>();
    connections.forEach(conn => {
      connectionCounts.set(conn.fromComponent, (connectionCounts.get(conn.fromComponent) || 0) + 1);
      connectionCounts.set(conn.toComponent, (connectionCounts.get(conn.toComponent) || 0) + 1);
    });

    const hubComponentId = Array.from(connectionCounts.entries())
      .sort(([,a], [,b]) => b - a)[0]?.[0];

    const hubComponent = components.find(comp =>
      comp.id.split('/').pop() === hubComponentId
    );
    const spokeComponents = components.filter(comp => comp !== hubComponent);

    const result: VisualizationNode[] = [];

    // Position hub at center
    if (hubComponent) {
      result.push({
        ...hubComponent,
        x: this.config.centerX,
        y: this.config.centerY
      });
    }

    // Position spoke components in a circle around hub
    const radius = this.config.baseSpacing * 1.5;
    spokeComponents.forEach((comp, index) => {
      const angle = (2 * Math.PI * index) / spokeComponents.length;
      result.push({
        ...comp,
        x: this.config.centerX + radius * Math.cos(angle),
        y: this.config.centerY + radius * Math.sin(angle)
      });
    });

    return result;
  }

  /**
   * Ring layout: Arrange components in a circular pattern
   */
  private applyRingLayout(
    components: VisualizationNode[],
    connections: ComponentConnection[]
  ): VisualizationNode[] {
    const radius = this.config.baseSpacing * Math.max(1, components.length / (2 * Math.PI));

    return components.map((comp, index) => {
      const angle = (2 * Math.PI * index) / components.length;
      return {
        ...comp,
        x: this.config.centerX + radius * Math.cos(angle),
        y: this.config.centerY + radius * Math.sin(angle)
      };
    });
  }

  /**
   * Grid layout: Arrange components in a rectangular grid (fallback)
   */
  private applyGridLayout(components: VisualizationNode[]): VisualizationNode[] {
    const columns = Math.ceil(Math.sqrt(components.length));
    const rows = Math.ceil(components.length / columns);

    // Center the grid
    const startX = this.config.centerX - ((columns - 1) * this.config.baseSpacing) / 2;
    const startY = this.config.centerY - ((rows - 1) * this.config.baseSpacing) / 2;

    return components.map((comp, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);

      return {
        ...comp,
        x: startX + col * this.config.baseSpacing,
        y: startY + row * this.config.baseSpacing
      };
    });
  }
}